import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { SOCKET_URL } from "../../../config/env";
import bszService from "../services";

export default function useBszSocket({
  setRooms,
  setServerNow,
  loadClassesOverview,
}) {
  const socketRef = useRef(null);

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      reconnection: true,
      reconnectionAttempts: 5,
      timeout: 2000,
    });

    socketRef.current = socket;

    /**
     * =========================================================
     * ✅ HELPER: FETCH + NORMALIZE ROOMS
     * =========================================================
     */
    const fetchRooms = async () => {
      const res = await bszService.getRooms();

      /**
       * ✅ Normalize response shape
       */
      const rooms = Array.isArray(res) ? res : res.rooms;
      const serverNow = Array.isArray(res)
        ? Date.now()
        : res.serverNow || Date.now();

      return { rooms, serverNow };
    };

    /**
     * =========================================================
     * ✅ RESERVATION UPDATE
     * =========================================================
     */
    const handleReservationUpdate = async () => {
      try {
        // console.log("⚡ Reservation socket event received");
        const { rooms, serverNow } = await fetchRooms();

        /**
         * ✅ FULL STATE REPLACEMENT (IMPORTANT)
         */
        setRooms(rooms);
        setServerNow(serverNow);
        await loadClassesOverview();
      } catch (err) {
        console.error("❌ reservation update failed:", err);
      }
    };

    /**
     * =========================================================
     * ✅ ROOM + LAYOUT UPDATES
     * =========================================================
     */
    const handleRoomUpdate = async ({ type, room, roomId, layout }) => {
      // console.log("🏠 room update:", { type, room, roomId, layout });

      /**
       * =========================================================
       * ✅ ALWAYS FULL REFRESH FOR CRITICAL CHANGES
       * =========================================================
       */
      if (type === "layoutRefresh" || type === "create" || type === "delete") {
        try {
          const { rooms, serverNow } = await fetchRooms();

          setRooms(rooms);
          setServerNow(serverNow);
        } catch (err) {
          console.error("❌ full refresh failed:", err);
        }
        return;
      }

      /**
       * =========================================================
       * ✅ PARTIAL PATCHES (SAFE CASES)
       * =========================================================
       */
      setRooms((prev) => {
        if (!Array.isArray(prev)) return [];

        switch (type) {
          /**
           * ✅ UPDATE META
           */
          case "update":
            return prev.map((r) => (r.id === room.id ? room : r));
          /**
           * ✅ LAYOUT PATCH (minor case)
           */
          case "layout":
            return prev.map((r) =>
              r.id === roomId
                ? {
                    ...r,
                    section: layout?.section ?? r.section,
                    layout_row: layout?.row ?? r.layout_row,
                    layout_col: layout?.col ?? r.layout_col,
                    layout_order: layout?.order ?? r.layout_order,
                  }
                : r,
            );

          default:
            return prev;
        }
      });
    };

    /**
     * =========================================================
     * ✅ REGISTER EVENTS
     * =========================================================
     */
    socket.on("bszReservationUpdated", handleReservationUpdate);
    socket.on("bszRoomUpdated", handleRoomUpdate);

    /**
     * =========================================================
     * ✅ CLEANUP
     * =========================================================
     */
    return () => {
      socket.off("bszReservationUpdated", handleReservationUpdate);
      socket.off("bszRoomUpdated", handleRoomUpdate);
      socket.disconnect();
    };
  }, [setRooms, setServerNow]);
}
